import { Controller, useFormContext } from 'react-hook-form'
import { Spinner, RadioButtonGroup, RadioButton, Input, Spacer } from '@cb/apricot-react'
import { CBInputMethod } from '@cb/apricot'
import { isEmpty } from '@myap/ui-library'
import Autosuggest from '../../../_common/forms/Autosuggest'
import SimpleErrorMsg from '../../../_common/SimpleErrorMsg'
import useStudentGetTribesQuery from '../../hooks/useStudentGetTribesQuery'
import { validateFieldState } from '../validations'
import {
  FIELD_RECOGNIZED_TRIBE,
  FIELD_DESCENDANT_TRIBE,
  FIELD_ENROLLED_TRIBE,
  FIELD_DISPLAY_TRIBENAME,
} from '../constants'

function NativeAmericanAlaskanTribe({ disabled }) {
  const { control, setValue, watch, register, trigger } = useFormContext()
  const [finishedInitializing, setFinishedInitializing] = useState(false)
  const recognizedTribe = watch(FIELD_RECOGNIZED_TRIBE)
  const enrolledTribe = watch(FIELD_ENROLLED_TRIBE)
  const tribeDisplayName = watch(FIELD_DISPLAY_TRIBENAME)

  // Fetch Tribes
  const { data: tribesData, loading, error } = useStudentGetTribesQuery({ isStudent: true })

  useEffect(() => {
    CBInputMethod()
  }, [])

  useEffect(() => {
    // Continue with rendering if no enrolledTribe has been selected
    if (!recognizedTribe) {
      setFinishedInitializing(true)
      // Else, hold on rendering Autosuggest till name of tribe has been found in tribe list
    } else if (
      !loading &&
      tribesData.length > 0 &&
      recognizedTribe &&
      enrolledTribe &&
      isEmpty(tribeDisplayName)
    ) {
      const { name } = tribesData.find(tribe => tribe.id === enrolledTribe)
      setValue(FIELD_DISPLAY_TRIBENAME, name)
      setFinishedInitializing(true)
    }
  }, [loading, tribesData, recognizedTribe, enrolledTribe, tribeDisplayName])

  useEffect(() => {
    if (!isEmpty(enrolledTribe)) {
      trigger(FIELD_DISPLAY_TRIBENAME)
    }
  }, [enrolledTribe])

  // Show spinner while tribes are loading
  if (!finishedInitializing || loading) {
    return (
      <>
        <Spacer size="16" />
        <Spinner size="24" />
      </>
    )
  }

  return (
    <>
      <input {...register(FIELD_ENROLLED_TRIBE, { shouldUnregister: true })} type="hidden" />
      <Spacer size="16" />
      <Controller
        control={control}
        name={FIELD_RECOGNIZED_TRIBE}
        disabled={disabled}
        shouldUnregister
        rules={{
          validate: value =>
            value !== true && value !== false ? 'Selection is required' : undefined,
        }}
        render={({ field: { onChange, onBlur, disabled, value, name } }) => {
          return (
            <RadioButtonGroup
              name={name}
              legend="Are you enrolled in a federally or state recognized tribe?"
              required
              vertical={true}
              onChange={onChange}
              value={value ?? false} // Keeps form from going from uncontrolled to controlled
            >
              <RadioButton
                name={name}
                checked={value === true}
                label="Yes"
                disabled={disabled}
                onChange={() => onChange(true)}
                onBlur={onBlur}
              />
              <RadioButton
                name={name}
                checked={value === false}
                label="No"
                disabled={disabled}
                onChange={() => {
                  setValue(FIELD_ENROLLED_TRIBE, null)
                  setValue(FIELD_DISPLAY_TRIBENAME, '')
                  onChange(false)
                }}
                onBlur={onBlur}
              />
            </RadioButtonGroup>
          )
        }}
      />
      {recognizedTribe ? (
        <>
          <Spacer size="24" />
          {finishedInitializing ? (
            <Controller
              control={control}
              name={FIELD_DISPLAY_TRIBENAME}
              disabled={disabled}
              shouldUnregister
              rules={{
                validate: () => {
                  if (isEmpty(enrolledTribe)) {
                    return 'You must select a tribe from the dropdown.'
                  }
                  return undefined
                },
              }}
              render={({
                field: { onChange, onBlur, value, disabled, name },
                fieldState: { invalid, isDirty, isTouched, error },
              }) => (
                <>
                  <Autosuggest
                    name={name}
                    label="In which tribe are you enrolled?"
                    value={value}
                    placeholder={loading ? 'Loading...' : 'Search by tribe name'}
                    disabled={disabled || loading}
                    required={true}
                    dropDownItemFunc={label => label}
                    callBackFunc={(label, value) => {
                      // function executed on select
                      setValue(FIELD_ENROLLED_TRIBE, value)
                      setValue(FIELD_DISPLAY_TRIBENAME, label)
                    }}
                    itemLabelFunc={(event, label, value) => label}
                    fetchSuggestions={searchTerm => {
                      const suggestions = tribesData
                        .filter(tribe => {
                          return tribe.name.toLowerCase().includes(searchTerm.toLowerCase())
                        })
                        .slice(0, 10)
                      return { suggestions, total: suggestions.length }
                    }}
                    processSuggestions={(suggestions = []) => {
                      return suggestions.map(({ id, name }) => ({ label: name, value: id }))
                    }}
                    onChange={value => {
                      // Clear the enrolledTribeCd when user makes changes to this field
                      setValue(FIELD_ENROLLED_TRIBE, null)
                      onChange(value)
                    }}
                    onBlur={() => {
                      onBlur()
                    }}
                    validation={validateFieldState({ invalid, isDirty, isTouched, error, value })}
                    validationMsg={error?.message}
                    scrollLimit={10}
                    cachedSuggestionsData={tribesData}
                  />
                  {error ? <SimpleErrorMsg error={error?.message} /> : null}
                </>
              )}
            />
          ) : null}
        </>
      ) : null}
      <Spacer size="24" />
      <Controller
        control={control}
        name={FIELD_DESCENDANT_TRIBE}
        disabled={disabled}
        shouldUnregister
        render={({ field: { onChange, onBlur, value, disabled, name } }) => (
          <Input
            name={name}
            label="Of which tribe(s) are you a descendant?"
            placeholder="Enter name of tribe(s) you are descended from"
            onChange={value => onChange(value)}
            onBlur={value => onBlur(value)}
            floating={false}
            autoComplete="off"
            maxLength="500"
            disabled={disabled}
            defaultValue={value}
          />
        )}
      />
    </>
  )
}

export default NativeAmericanAlaskanTribe
